import React from "react";
import { motion } from "framer-motion";
import {
  ScanSearch, CalendarClock, FileStack, Scale, MessageSquareText,
  FolderTree, RefreshCw, Brain,
} from "lucide-react";
import { StaggerGroup, staggerItem, MaskReveal } from "./Reveal";

const FEATURES = [
  { icon: ScanSearch, title: "Document Review", desc: "DOX reads every upload — OCR-assisted understanding of PDFs, licenses, permits and insurance certificates.", span: "lg:col-span-7", big: true },
  { icon: CalendarClock, title: "Deadline Monitoring", desc: "Never miss a renewal. DOX tracks expirations and surfaces what's due next.", span: "lg:col-span-5" },
  { icon: FileStack, title: "Paperwork Preparation", desc: "Form completion assistance so filings are ready to go.", span: "lg:col-span-5" },
  { icon: Scale, title: "Compliance Reasoning", desc: "Recommendations verified against official sources — with confidence levels and source attribution. Never invented requirements.", span: "lg:col-span-7", big: true },
  { icon: MessageSquareText, title: "Plain-English Explanations", desc: "No legalese. DOX explains what every document means.", span: "lg:col-span-4" },
  { icon: FolderTree, title: "Intelligent Categorization", desc: "Auto-sorted by license, permit, insurance, bond and more.", span: "lg:col-span-4" },
  { icon: RefreshCw, title: "Renewal Recommendations", desc: "Proactive guidance on what to renew and when.", span: "lg:col-span-4" },
  { icon: Brain, title: "Business Profile Memory", desc: "DOX remembers your business, so it gets smarter with every document you add.", span: "lg:col-span-12" },
];

export default function Features() {
  return (
    <section id="features" className="relative py-28 sm:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-16 sm:mb-20">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-dox-orange mb-5">What DOX Does</div>
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.02]">
            <MaskReveal>An entire back office,</MaskReveal>
            <br />
            <MaskReveal delay={0.15}><span className="text-gradient-brand">working while you build.</span></MaskReveal>
          </h2>
        </div>

        <StaggerGroup className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
          {FEATURES.map((f) => (
            <motion.div
              key={f.title}
              variants={staggerItem}
              className={`group relative glass-card p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 ${f.span}`}
            >
              <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-dox-orange/0 group-hover:bg-dox-orange/10 blur-3xl transition-all duration-500" />
              <div className="relative">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl glass border border-white/10 mb-6 text-dox-orange group-hover:scale-110 group-hover:text-dox-magenta transition-all duration-300">
                  <f.icon size={22} />
                </div>
                <h3 className={`font-display font-bold text-white mb-3 tracking-tight ${f.big ? "text-2xl" : "text-xl"}`}>{f.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed max-w-lg">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}
